import type { DatumEntity } from "building-types/datums/datumEntityTypes";
import { DatumType } from "building-types/datums/datumEntityTypes";
import type { Doc } from "building-types/doc";
import type { Group } from "building-types/groups/group";
import type { SettingsSet } from "building-types/settings/settingsSet";
import { SettingsSetBehaviorType } from "building-types/settings/settingsSetBehaviorType";
import type { SheetTemplate, DrawingTemplate, ContentTemplate } from "building-types/views/planSets";
import type { RWTable } from "common/data/table";
import type { DocTableFieldNames } from "../docTableFieldNames";
import { docEntityGetSortedChildrenEntities } from "./docEntityGetChildrenEntities";
import type { GroupableEntity } from "./docEntityGroupable";
import type { GroupedAndUngroupedEntitiesBySubType } from "./groupedAndUngroupedEntityList";

/**
 * Certain groupable entity types are unique in that they are groupable
 * and/or distinguished by their sub-types. The discriminating sub-type fields
 * are often unique to the entity type, so require special handling.
 */

export function docGetGroupedAndUngroupedEntitiesBySubType(
  doc: Doc,
  docTableName: DocTableFieldNames,
  sortedEntitiesAndGroups: (GroupableEntity | Group)[],
): GroupedAndUngroupedEntitiesBySubType[keyof GroupedAndUngroupedEntitiesBySubType] | (GroupableEntity | Group)[] {
  switch (docTableName) {
    case "contentTemplates": {
      const sheetTemplates: (SheetTemplate | Group)[] = [];
      const drawingTemplates: (DrawingTemplate | Group)[] = [];

      const contentTemplateGroups = sortedEntitiesAndGroups as (ContentTemplate | Group)[];

      for (const entity of contentTemplateGroups) {
        if (entity.entityType === "GROUP") {
          const children = docEntityGetSortedChildrenEntities(doc, { type: "GROUP", id: entity.id }) as (
            | SheetTemplate
            | DrawingTemplate
          )[];
          const childrenTemplateType = children[0].templateType;

          if (childrenTemplateType === "SHEET") {
            sheetTemplates.push(entity);
          } else if (childrenTemplateType === "DRAWING") {
            drawingTemplates.push(entity);
          }
          continue;
        }

        if (entity.templateType === "SHEET") {
          sheetTemplates.push(entity);
        } else if (entity.templateType === "DRAWING") {
          drawingTemplates.push(entity);
        }
      }

      return {
        sheetTemplates,
        drawingTemplates,
      };
    }

    case "settingsSets": {
      const targetedSettingsSets: (SettingsSet | Group)[] = [];
      const globalSettingsSets: (SettingsSet | Group)[] = [];

      const settingsSets = sortedEntitiesAndGroups as (SettingsSet | Group)[];

      // Skip empty names -- commonly used for organizational purposes by users to separate settings sets
      const filterInvalidNames = (entity: SettingsSet | Group) => {
        return !!entity.name?.trim().length;
      };

      for (const entity of settingsSets) {
        if (entity.entityType === "GROUP") {
          const children = docEntityGetSortedChildrenEntities(doc, { type: "GROUP", id: entity.id }) as SettingsSet[];
          const childrenSettingsSetType = children[0].type;

          if (childrenSettingsSetType === SettingsSetBehaviorType.GLOBAL) {
            globalSettingsSets.push(entity);
          } else {
            targetedSettingsSets.push(entity);
          }
          continue;
        }

        if (!filterInvalidNames(entity)) {
          continue;
        }

        if (entity.type === SettingsSetBehaviorType.GLOBAL) {
          globalSettingsSets.push(entity);
        } else {
          targetedSettingsSets.push(entity);
        }
      }

      return {
        globalSettingsSets,
        targetedSettingsSets,
      };
    }

    case "datums": {
      const datums = sortedEntitiesAndGroups as (DatumEntity | Group)[];

      const datumsBySubType: RWTable<(DatumEntity | Group)[]> = {};

      const addDatumOrGroupToTable = (
        table: RWTable<(DatumEntity | Group)[]>,
        datumType: DatumType,
        entity: DatumEntity | Group,
      ) => {
        if (!table[datumType]) {
          table[datumType] = [];
        }
        table[datumType].push(entity);
      };

      const addDatumToTable = (table: RWTable<(DatumEntity | Group)[]>, datum: DatumEntity) => {
        addDatumOrGroupToTable(table, datum.datumType, datum);
      };

      for (const entity of datums) {
        if (entity.entityType === "GROUP") {
          const children = docEntityGetSortedChildrenEntities(doc, { type: "GROUP", id: entity.id }) as DatumEntity[];

          const childDatumsBySubType: RWTable<(DatumEntity | Group)[]> = {};
          for (const child of children) {
            addDatumToTable(childDatumsBySubType, child);
          }

          for (const subType in childDatumsBySubType) {
            if (!childDatumsBySubType[subType] || childDatumsBySubType[subType].length === 0) {
              continue;
            }

            addDatumOrGroupToTable(datumsBySubType, subType as DatumType, entity);
          }

          continue;
        }

        addDatumToTable(datumsBySubType, entity);
      }

      return {
        levelDatums: datumsBySubType[DatumType.LEVEL] ?? [],
        wallDatums: datumsBySubType[DatumType.WALL] ?? [],
        foundationDatums: datumsBySubType[DatumType.FOUNDATION] ?? [],
        shareableDatums: datumsBySubType[DatumType.SHAREABLE] ?? [],
      };
    }

    default:
      return sortedEntitiesAndGroups;
  }
}
